/** Standalone registers over the same academic records visited from a course. */
import { Link, Navigate, useLocation, useSearchParams } from "react-router";
import { Bento, PageRefusal } from "@/components/ui/box";
import { AcademicPageHeader } from "@/components/records/AcademicWorkspace";
import { RegistrarLocalNavigation } from "@/components/registrar/RegistrarLocalNavigation";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaceEyebrow } from "@/hooks/use-workspace-eyebrow";
import { useStructureModels, type StructureKind } from "@/components/academic-structure/use-structure-models";
import { structureRegister } from "@/components/academic-structure/structure-register";
import { structureCopy as words } from "./academic-structure-copy";

const categories: { kind: StructureKind; tab: string; description: string }[] = [
  { kind: "program", tab: "programs", description: "Programs place courses in the institution's academic offering." },
  { kind: "semester", tab: "semesters", description: "Terms hold course dates and the boundary for publication and enrollment." },
  { kind: "department", tab: "departments", description: "Departments file programs and name their faculty lead." },
  { kind: "cohort", tab: "cohorts", description: "Cohorts group learners for placement and enrollment." },
];
export function AcademicStructurePage() {
  const { me } = useAuth();
  return <Structure key={`${me?.tenant_id}/${me?.user_id}`} />;
}
function Structure() {
  const eyebrow = useWorkspaceEyebrow();
  const location = useLocation();
  const [params] = useSearchParams();
  const { lists, creates, allRows, refused } = useStructureModels();
  const active = categories.find(category => category.tab === params.get("tab") || category.kind === params.get("tab") || category.kind === "semester" && params.get("tab") === "terms")?.kind ?? (params.has("term") ? "semester" : "program");
  // Existing term-review links now open that same exact record and keep Return.
  if (params.get("term")) {
    const next = new URLSearchParams(params); next.delete("term"); next.set("tab", "semesters");
    if (!next.has("visit")) next.set("visit", `term:${params.get("term")}/review`);
    return <Navigate replace to={`${location.pathname}?${next}${location.hash}`} />;
  }
  return <div className="app-page">
    <AcademicPageHeader eyebrow={eyebrow} title={words.title} description="Programs and terms place courses. Departments file programs. Cohorts group learners for enrollment." />
    <RegistrarLocalNavigation area="courses" />
    {refused ? <PageRefusal id="academic-structure" journey="academic-structure" refusal={{ reason: "forbidden", sentence: "You don't have access to these records.", back: { label: "Courses", to: "/admin/courses" } }} /> : <>
      <nav aria-label="Academic records" className="flex min-w-0 flex-wrap gap-2">
        {categories.map(({ kind, tab }) => {
          const next = new URLSearchParams(params); next.set("tab", tab); next.delete("term"); next.delete("visit");
          const list = lists[kind], create = creates[kind];
          const unknown = create.result?.state === "unknown" || allRows[kind].some(row => list.row.state?.(row)?.class === "failed");
          const draft = create.draft || list.draft;
          return <Link key={kind} to={`${location.pathname}?${next}${location.hash}`} aria-current={active === kind ? "page" : undefined} className={`inline-flex min-h-[var(--control-height-compact)] flex-wrap items-center gap-2 rounded-control px-3 text-ui font-semibold focus-visible:ring-2 focus-visible:ring-ring ${active === kind ? "bg-primary-wash text-primary" : "text-muted-foreground hover:bg-well"}`}>
            {words.kinds[kind].plural}{" "}{unknown ? <span className="text-meta text-status-warning-foreground">Not confirmed</span> : draft ? <span className="text-meta">Draft kept</span> : null}
          </Link>;
        })}
      </nav>
      {categories.map(({ kind, description }) => <section key={kind} hidden={active !== kind} inert={active !== kind} aria-label={words.kinds[kind].plural}>
        <p className="mb-3 text-ui text-muted-foreground">{description}</p>
        <Bento id={`academic-structure-${kind}`} journey="academic-structure" boxes={[creates[kind], structureRegister(kind, lists[kind])]} />
      </section>)}
    </>}
  </div>;
}
